import { useMemo } from "react";
import { PATHS } from "../../utills/paths";
import { useAuthContext } from "../user/AuthProvider";
import { useNavigate, useLocation } from "react-router-dom";
import { Flex, Button, Dropdown, Avatar, Typography } from "antd";
import { getUserAccessRole } from "../../utills/dashboard_functions";
import CustomIcon from "../layouts/CustomIcon";

const { Text, Paragraph } = Typography;

export default function WorkspaceSelection({ showWorkspaceName = false, currentOrganisationsOnly = false }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user, workspace, organization, switchWorkspace, planInfo, userAccess } = useAuthContext();

  const { teamManagement } = organization || {};
  const { isCustomPlan = false, isPlanCancelled = false } = planInfo || {};
  const { isOrgAdminOrOwner = false } = userAccess || {};
  const showUpgradeCta = !isCustomPlan && isOrgAdminOrOwner && !isPlanCancelled && !teamManagement?.enabled;

  const workspaces = useMemo(
    () => (currentOrganisationsOnly ? organization?.workspaces : user?.workspaces),
    [user, organization, currentOrganisationsOnly]
  );

  // Workspace Items
  const items = (workspaces || [])?.map(({ id, name, access }) => {
    const isActive = Number(workspace?.id) === Number(id);

    return {
      key: id,
      disabled: isActive,
      className: `cursor-${isActive ? "default" : "pointer"}`,
      onClick: () => {
        if (isActive) return;

        // Switch Workspace
        if (currentOrganisationsOnly) switchWorkspace(id, true);
        else navigate(PATHS.V2.switchWorkspaceRoute(id, pathname));
      },
      label: (
        <WorkspaceName
          name={name}
          access={access}
          extraWidth={16}
          extra={isActive ? <CustomIcon name='check' className='text-2E62E9' /> : null}
        />
      ),
    };
  });

  if (items?.length < 1) return null;
  if (items?.length < 2 && !showUpgradeCta) return null;

  const triggerButton = (
    <Button
      iconPlacement='end'
      type={showWorkspaceName ? "default" : "text"}
      icon={<CustomIcon name='chevron_selector_vertical' height={20} width={20} />}
    >
      {showWorkspaceName ? workspace?.name : ""}
    </Button>
  );

  const dropdownCommonProps = {
    trigger: ["click"],
    classNames: { root: "workspace-selection-dropdown" },
    placement: showWorkspaceName ? "bottomRight" : "bottom",
    styles: { root: { width: 300, ...(!showWorkspaceName ? { left: 20 } : {}) } },
  };

  // Single workspace with upgrade CTA
  if (items?.length < 2) {
    return (
      <Dropdown
        {...dropdownCommonProps}
        popupRender={(menu) => (
          <div className='workspace-selection-single-popup'>
            {menu}
            <Flex align='center' gap={8} style={{ padding: "4px 16px 12px" }}>
              <Button
                size='small'
                className='workspace-upgrade-btn'
                icon={<CustomIcon name='diamond_01' width={14} height={14} />}
                onClick={() => navigate(PATHS.V2.PLANS)}
              >
                Upgrade to Pro
              </Button>
              <Text type='secondary' className='font-size-12px'>
                to invite collaborators
              </Text>
            </Flex>
          </div>
        )}
        menu={{ items }}
      >
        {triggerButton}
      </Dropdown>
    );
  }

  // Multiple workspaces
  return (
    <Dropdown
      {...dropdownCommonProps}
      menu={{
        items: [
          {
            type: "group",
            children: items,
            style: { paddingBottom: 8 },
            label: (
              <Text type='secondary' className='font-size-12px'>
                Switch Workspace
              </Text>
            ),
          },
        ],
      }}
    >
      {triggerButton}
    </Dropdown>
  );
}

// Workspace Name
export function WorkspaceName({ access = {}, name = "", extra = null, className = "", extraWidth = 0, redirectToWorkspace = false }) {
  const navigate = useNavigate();

  const [first = "", last = ""] = (name || "").split(" ");
  const avatarText = first ? first[0] + (last?.[0] || first?.[1] || "") : "";
  const hasAccess = Object.keys(access || {})?.length > 0;

  return (
    <Flex gap={8} className={className} align='center' style={{ overflow: "hidden" }}>
      <Avatar
        size={42}
        shape='square'
        className={redirectToWorkspace ? "cursor-pointer" : ""}
        onClick={() => redirectToWorkspace && navigate(PATHS.V2.WORKSPACE)}
        style={{
          flexShrink: 0,
          border: "1px solid",
          background: "linear-gradient(226.62deg, #FEDBC4 29.15%, #93B0FC 74.77%, #547EED 99.89%)",
        }}
      >
        <span className='fw-500 font-size-14px text-uppercase'>{avatarText}</span>
      </Avatar>
      <Flex vertical style={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
        <Paragraph ellipsis={{ tooltip: true }} className='m-0 fw-600 font-size-16px'>
          {name}
        </Paragraph>

        {hasAccess && (
          <Text type='secondary' className='fw-400 font-size-12px'>
            {getUserAccessRole(access)}
          </Text>
        )}
      </Flex>

      {extra}
    </Flex>
  );
}
